import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { AppModal } from '../../components/AppModal'
import {
  PrimaryButton,
  DangerButton,
  colors
} from '../../theme/sharedComponents'

interface SessionWarningModalProps {
  show: boolean
  remainingSeconds: number
  onExtendSession: () => Promise<void>
  onLogout: () => void
  onHide: () => void
}

const CountdownText = styled.div`
  font-size: 3rem;
  font-weight: 700;
  color: ${colors.warning};
  text-align: center;
  margin: 1.5rem 0;
  font-family: 'Courier New', monospace;
  letter-spacing: 0.1em;
`

const WarningText = styled.div`
  text-align: center;
  color: ${colors.textLightModeMuted};
  font-size: 1rem;
  margin-bottom: 1.5rem;
  line-height: 1.6;
`

const ButtonContainer = styled.div`
  display: grid;
  gap: 0.75rem;
  margin-top: 1.5rem;

  button {
    padding: 0.75rem 1.5rem;
    font-size: 1rem;
  }
`

const SessionWarningModal: React.FC<SessionWarningModalProps> = ({
  show,
  remainingSeconds,
  onExtendSession,
  onLogout,
  onHide
}) => {
  const [extending, setExtending] = useState(false)
  const [countdown, setCountdown] = useState(remainingSeconds)
  const [error, setError] = useState<string | null>(null)

  // Update countdown from prop
  useEffect(() => {
    setCountdown(remainingSeconds)
  }, [remainingSeconds])

  // Format seconds as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleExtendSession = async () => {
    setExtending(true)
    setError(null)
    try {
      await onExtendSession()
      onHide() // Close modal on success
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to extend session. Please try again or log out.')
    } finally {
      setExtending(false)
    }
  }

  const handleLogout = () => {
    onLogout()
    onHide()
  }

  return (
    <AppModal
      show={show}
      onHide={onHide}
      title="Session Expiring Soon"
      icon="bi bi-exclamation-triangle-fill"
      noDismiss
      error={error}
    >
      <>
        <WarningText>
          Your session will expire due to inactivity. You'll be automatically logged out in:
        </WarningText>

        <CountdownText>
          {formatTime(countdown)}
        </CountdownText>

        <WarningText>
          Click <strong>"Stay Logged In"</strong> to continue your session, or <strong>"Log Out"</strong> to end it now.
        </WarningText>

        <ButtonContainer>
          <PrimaryButton onClick={handleExtendSession} disabled={extending}>
            {extending ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Extending Session...
              </>
            ) : (
              <>
                <i className="bi bi-clock-history me-2"></i>
                Stay Logged In
              </>
            )}
          </PrimaryButton>
          <DangerButton onClick={handleLogout} disabled={extending}>
            <i className="bi bi-box-arrow-right me-2"></i>
            Log Out Now
          </DangerButton>
        </ButtonContainer>
      </>
    </AppModal>
  )
}

export default SessionWarningModal
